import { ButtonBase, InputBase, MenuItem, Popover, Tab, Tabs } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, styled } from '@mui/material/styles';
import { getThemeSpacingCssPx, getThemeTypographyCssPx, getThemeTypographyPx } from 'components/ui/utilities/theme';
import { getCssPx, getPortalZoomScale } from 'components/ui/utilities/zoom';
import { getPrismScrollbarStyles, PRISM_BREAKPOINTS } from 'theme';
import type {
  PrimaryNavigationChipValueTone,
  PrimaryNavigationMenuActionTone,
  PrimaryNavigationMenuItemTone,
} from './PrimaryNavigation.props';

interface ZoomStyleProps {
  zoomLevel: number;
}

interface PrimaryNavigationIconButtonStyleProps extends ZoomStyleProps {
  isInteractive: boolean;
}

interface PrimaryNavigationChipValueStyleProps extends ZoomStyleProps {
  valueTone: PrimaryNavigationChipValueTone;
}

interface PrimaryNavigationMenuItemStyleProps {
  isActive: boolean;
  isHeader: boolean;
  tone: PrimaryNavigationMenuItemTone;
}

interface PrimaryNavigationMenuActionStyleProps {
  tone: PrimaryNavigationMenuActionTone;
}

interface PrimaryNavigationMenuItemLeadingActionStyleProps {
  isDisabled?: boolean;
  isVisible: boolean;
}

interface PrimaryNavigationMenuStyleProps {
  menuMaxWidth: number;
  menuMinWidth: number;
  menuWidth: 'default' | 'content';
  zoomLevel: number;
}

const PRIMARY_NAVIGATION_TABLET_BREAKPOINT_PX = PRISM_BREAKPOINTS.tablet1024;
export const PRIMARY_NAVIGATION_HEIGHT_PX = 48;
const PRIMARY_NAVIGATION_AI_ICON_SRC = '/images/scalar_ai_white.png';
const PRIMARY_NAVIGATION_MENU_VIEWPORT_GUTTER_PX = 96;
export const PRIMARY_NAVIGATION_SEARCH_BUTTON_WIDTH_PX = 208;
export const PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR = '--primary-navigation-menu-zoom-scale';
const PRIMARY_NAVIGATION_TAB_LINE_HEIGHT_PX = 16;
const PRIMARY_NAVIGATION_TAB_PADDING_Y_PX = 7;

const shouldForwardZoomProp = (prop: PropertyKey) => prop !== 'zoomLevel';
const shouldForwardIconButtonProp = (prop: PropertyKey) => prop !== 'zoomLevel' && prop !== 'isInteractive';
const shouldForwardChipValueProp = (prop: PropertyKey) => prop !== 'zoomLevel' && prop !== 'valueTone';
const shouldForwardMenuItemProp = (prop: PropertyKey) => prop !== 'isActive' && prop !== 'isHeader' && prop !== 'tone';
const shouldForwardMenuActionProp = (prop: PropertyKey) => prop !== 'tone';
const shouldForwardMenuItemLeadingActionProp = (prop: PropertyKey) => prop !== 'isDisabled' && prop !== 'isVisible';
const shouldForwardMenuProp = (prop: PropertyKey) =>
  prop !== 'menuMaxWidth' && prop !== 'menuMinWidth' && prop !== 'menuWidth' && prop !== 'zoomLevel';

const getPrimaryNavigationMenuScaledPx = (value: number) =>
  `calc(${value}px * var(${PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR}, 1))`;

const getPrimaryNavigationMenuScaledSpacingPx = (theme: Theme, spacingValue: number) =>
  `calc(${theme.spacing(spacingValue)} * var(${PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR}, 1))`;

const getPrimaryNavigationMenuScaledTypographyPx = (
  theme: Theme,
  variant: Parameters<typeof getThemeTypographyPx>[1],
  property: Parameters<typeof getThemeTypographyPx>[2]
) => getPrimaryNavigationMenuScaledPx(getThemeTypographyPx(theme, variant, property));

const getPrimaryNavigationTransition = (theme: Theme) =>
  theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
    duration: 160,
    easing: theme.transitions.easing.easeInOut,
  });

const getPrimaryNavigationMenuItemBackgroundColor = (
  { isActive, isHeader, tone }: PrimaryNavigationMenuItemStyleProps,
  theme: Theme
) => {
  if (isHeader) {
    return 'transparent';
  }
  if (isActive) {
    return theme.palette.navigation.primary.menu.item.activeBackground;
  }
  if (tone === 'ai') {
    return theme.palette.navigation.primary.menu.surface;
  }
  if (tone === 'action') {
    return 'transparent';
  }
  if (tone === 'danger') {
    return 'transparent';
  }
  return 'transparent';
};

const getPrimaryNavigationMenuItemHoverBackgroundColor = (
  { isHeader, tone }: PrimaryNavigationMenuItemStyleProps,
  theme: Theme
) => {
  if (isHeader) {
    return 'transparent';
  }

  if (tone === 'action') {
    return alpha(theme.palette.primary.main, 0.06);
  }

  if (tone === 'danger') {
    return theme.palette.navigation.primary.menu.item.danger.hoverBackground;
  }

  if (tone === 'ai') {
    return 'transparent';
  }

  return theme.palette.navigation.primary.menu.item.hoverBackground;
};

const getPrimaryNavigationMenuItemFocusBackgroundColor = (
  { isHeader, tone }: PrimaryNavigationMenuItemStyleProps,
  theme: Theme
) => {
  if (isHeader) {
    return 'transparent';
  }

  if (tone === 'action') {
    return alpha(theme.palette.primary.main, 0.1);
  }

  if (tone === 'danger') {
    return theme.palette.navigation.primary.menu.item.danger.focusBackground;
  }

  if (tone === 'ai') {
    return 'transparent';
  }

  return theme.palette.navigation.primary.menu.item.activeBackground;
};

const getPrimaryNavigationMenuItemColor = (
  { isActive, isHeader, tone }: PrimaryNavigationMenuItemStyleProps,
  theme: Theme
) => {
  if (isHeader) {
    return theme.palette.navigation.primary.menu.item.header;
  }

  if (isActive) {
    return theme.palette.navigation.primary.menu.item.primaryText;
  }

  if (tone === 'action') {
    return theme.palette.primary.main;
  }

  if (tone === 'danger') {
    return theme.palette.navigation.primary.menu.item.danger.text;
  }

  if (tone === 'ai') {
    return theme.palette.navigation.primary.menu.item.secondaryText;
  }

  return theme.palette.navigation.primary.menu.item.secondaryText;
};

const getPrimaryNavigationMenuItemInteractionColor = (
  { isHeader, tone }: Pick<PrimaryNavigationMenuItemStyleProps, 'isHeader' | 'tone'>,
  theme: Theme
) => {
  if (isHeader) {
    return theme.palette.navigation.primary.menu.item.header;
  }

  if (tone === 'action') {
    return theme.palette.primary.main;
  }

  if (tone === 'danger') {
    return theme.palette.navigation.primary.menu.item.danger.interactionText;
  }

  if (tone === 'ai') {
    return theme.palette.navigation.primary.menu.item.secondaryText;
  }

  return theme.palette.navigation.primary.menu.item.primaryText;
};

const getPrimaryNavigationMenuItemDisabledColor = (
  { isHeader, tone }: Pick<PrimaryNavigationMenuItemStyleProps, 'isHeader' | 'tone'>,
  theme: Theme
) => {
  if (isHeader) {
    return theme.palette.navigation.primary.menu.item.header;
  }

  if (tone === 'danger') {
    return theme.palette.navigation.primary.menu.item.danger.disabledText;
  }

  return theme.palette.navigation.primary.menu.item.disabled;
};

const getPrimaryNavigationMenuItemFontWeight = (
  { isHeader, tone }: Pick<PrimaryNavigationMenuItemStyleProps, 'isHeader' | 'tone'>,
  theme: Theme
) => {
  if (isHeader) {
    return theme.typography.fontWeightBold;
  }

  if (tone === 'action') {
    return theme.typography.fontWeightMedium;
  }

  return theme.typography.fontWeightRegular;
};

const getPrimaryNavigationMenuItemFontStyle = ({ tone }: Pick<PrimaryNavigationMenuItemStyleProps, 'tone'>) =>
  tone === 'muted' ? 'italic' : 'normal';

const getPrimaryNavigationChipTextStyles = (theme: Theme) => ({
  fontSize: getThemeTypographyCssPx(theme, 'body2', 'fontSize'),
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightRegular,
  letterSpacing: 0,
  lineHeight: getThemeTypographyCssPx(theme, 'body2', 'lineHeight'),
});

export const PrimaryNavigationRoot = styled('nav', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  '--primary-navigation-active-indicator-height': getCssPx(1),
  display: 'flex',
  alignItems: 'stretch',
  height: getCssPx(PRIMARY_NAVIGATION_HEIGHT_PX),
  boxSizing: 'border-box',
  backgroundColor: theme.palette.navigation.primary.bar.background,
  padding: `${getThemeSpacingCssPx(theme, 's')} ${getThemeSpacingCssPx(theme, 'm')}`,
  flexShrink: 0,
  [`@media (max-width: ${PRIMARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    alignItems: 'center',
    flexWrap: 'wrap',
    height: 'auto',
    minHeight: getCssPx(PRIMARY_NAVIGATION_HEIGHT_PX),
    rowGap: getThemeSpacingCssPx(theme, 's'),
  },
}));

export const PrimaryNavigationSection = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

export const PrimaryNavigationBrand = styled(PrimaryNavigationSection, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  height: getCssPx(32),
  gap: getThemeSpacingCssPx(theme, 's'),
  flexShrink: 0,
  minWidth: 0,
  [`@media (max-width: ${PRIMARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    flex: '1 1 0',
    order: 1,
  },
}));

export const PrimaryNavigationBrandSlot = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: getCssPx(32),
  height: getCssPx(32),
  borderRadius: getCssPx(theme.shape.radius.xs),
  flexShrink: 0,
  padding: getCssPx(1),
  '& > *': {
    alignItems: 'center',
    display: 'inline-flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  '& img, & svg': {
    display: 'block',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    maxWidth: '100%',
    objectFit: 'contain',
  },
}));

export const PrimaryNavigationContext = styled(PrimaryNavigationSection, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  flex: 1,
  gap: getThemeSpacingCssPx(theme, 's'),
  marginLeft: getThemeSpacingCssPx(theme, 's'),
  minWidth: 0,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  [`@media (max-width: ${PRIMARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    flexBasis: '100%',
    marginLeft: 0,
    order: 3,
    width: '100%',
  },
}));

export const PrimaryNavigationActions = styled(PrimaryNavigationSection, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  marginLeft: 'auto',
  gap: getThemeSpacingCssPx(theme, 's'),
  flexShrink: 0,
  [`@media (max-width: ${PRIMARY_NAVIGATION_TABLET_BREAKPOINT_PX}px)`]: {
    order: 2,
  },
}));

export const PrimaryNavigationTabsFrame = styled('div')({
  alignSelf: 'stretch',
  display: 'flex',
  flex: '1 1 auto',
  minWidth: 0,
  position: 'relative',
});

export const PrimaryNavigationActiveIndicator = styled('span')(({ theme }) => ({
  backgroundColor: theme.palette.navigation.primary.tab.activeUnderline,
  borderRadius: 'var(--primary-navigation-active-indicator-height, 1px)',
  bottom: 0,
  display: 'block',
  height: 'var(--primary-navigation-active-indicator-height, 1px)',
  left: 0,
  opacity: 'var(--active-navigation-indicator-opacity, 0)',
  pointerEvents: 'none',
  position: 'absolute',
  transform: 'translateX(var(--active-navigation-indicator-x, 0px))',
  transition: theme.transitions.create(['opacity', 'transform', 'width'], {
    duration: 220,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: 'var(--active-navigation-indicator-width, 0px)',
  zIndex: 0,
}));

export const PrimaryNavigationTabs = styled(Tabs)({
  flex: '1 1 auto',
  minWidth: 0,
  minHeight: 'unset',
  alignSelf: 'stretch',
  position: 'relative',
  zIndex: 1,
  '& .MuiTabs-scroller': {
    display: 'flex',
    alignItems: 'stretch',
    overflowX: 'auto !important',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '& .MuiTabs-flexContainer': {
    height: '100%',
    alignItems: 'stretch',
    minWidth: 'max-content',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
});

export const PrimaryNavigationSkeletonTabs = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flex: '1 1 auto',
  gap: getThemeSpacingCssPx(theme, 's'),
  height: getCssPx(32),
  minWidth: 0,
  overflow: 'hidden',
}));

export const PrimaryNavigationTab = styled(Tab, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.navigation.primary.tab.default,
  height: getCssPx(32),
  boxSizing: 'border-box',
  fontSize: getThemeTypographyCssPx(theme, 'caption', 'fontSize'),
  fontWeight: theme.typography.fontWeightMedium,
  justifyContent: 'center',
  lineHeight: getCssPx(PRIMARY_NAVIGATION_TAB_LINE_HEIGHT_PX),
  letterSpacing: 0,
  textTransform: 'none',
  minHeight: 'unset',
  minWidth: 'unset',
  overflow: 'visible',
  padding: `${getCssPx(PRIMARY_NAVIGATION_TAB_PADDING_Y_PX)} ${getThemeSpacingCssPx(theme, 's')}`,
  borderBottom: `${getCssPx(1)} solid transparent`,
  opacity: 1,
  transition: getPrimaryNavigationTransition(theme),
  '&.Mui-selected': {
    color: theme.palette.navigation.primary.tab.active,
    borderBottomColor: 'transparent',
  },
  '&.Mui-selected.Mui-disabled': {
    color: theme.palette.navigation.primary.tab.active,
    borderBottomColor: 'transparent',
  },
  '&.Mui-disabled': {
    color: theme.palette.navigation.primary.tab.default,
    opacity: 1,
  },
  '&:hover:not(.Mui-disabled)': {
    color: theme.palette.navigation.primary.tab.hover,
  },
}));

export const PrimaryNavigationTabLabel = styled('span')({
  display: 'block',
  lineHeight: 'inherit',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const PrimaryNavigationChipRoot = styled('div', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: getThemeSpacingCssPx(theme, 'xxs'),
  height: getCssPx(24),
  maxWidth: getCssPx(380),
  boxSizing: 'border-box',
  color: theme.palette.navigation.primary.chip.icon,
  ...getPrimaryNavigationChipTextStyles(theme),
  padding: getThemeSpacingCssPx(theme, 'xxs'),
  borderRadius: getCssPx(theme.shape.radius.xs),
  backgroundColor: theme.palette.navigation.primary.chip.background,
  cursor: 'pointer',
  flexShrink: 0,
  minWidth: 0,
  transition: getPrimaryNavigationTransition(theme),
  userSelect: 'none',
  '& svg': {
    fontSize: getCssPx(16),
  },
  '&[aria-disabled="true"]': {
    cursor: 'not-allowed',
    opacity: 0.62,
  },
  '&:hover': {
    backgroundColor: theme.palette.navigation.primary.chip.hoverBackground,
  },
  '&[aria-disabled="true"]:hover': {
    backgroundColor: theme.palette.navigation.primary.chip.background,
  },
}));

export const PrimaryNavigationChipIcon = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: getCssPx(16),
  height: getCssPx(16),
  color: theme.palette.navigation.primary.chip.icon,
  '& svg': {
    fontSize: getCssPx(16),
  },
}));

export const PrimaryNavigationChipLabel = styled('span', {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: getCssPx(20),
  marginRight: getThemeSpacingCssPx(theme, 'xxs'),
  color: theme.palette.navigation.primary.chip.label,
  ...getPrimaryNavigationChipTextStyles(theme),
  whiteSpace: 'nowrap',
}));

export const PrimaryNavigationChipValue = styled('span', {
  shouldForwardProp: shouldForwardChipValueProp,
})<PrimaryNavigationChipValueStyleProps>(({ theme, valueTone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: getCssPx(20),
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.palette.navigation.primary.chip.value[valueTone],
  ...getPrimaryNavigationChipTextStyles(theme),
  whiteSpace: 'nowrap',
}));

export const PrimaryNavigationChipDropdownIcon = styled(PrimaryNavigationChipIcon)({});

export const PrimaryNavigationIconButtonRoot = styled(ButtonBase, {
  shouldForwardProp: shouldForwardIconButtonProp,
})<PrimaryNavigationIconButtonStyleProps>(({ isInteractive, theme }) => ({
  width: getCssPx(32),
  height: getCssPx(32),
  boxSizing: 'border-box',
  color: theme.palette.navigation.primary.iconButton.color,
  padding: getThemeSpacingCssPx(theme, 'xs'),
  borderRadius: getCssPx(theme.shape.radius.s),
  cursor: isInteractive ? 'pointer' : 'default',
  pointerEvents: isInteractive ? 'auto' : 'none',
  transition: getPrimaryNavigationTransition(theme),
  '& svg': {
    fontSize: getCssPx(24),
  },
  '&:hover': {
    backgroundColor: isInteractive
      ? alpha(theme.palette.navigation.primary.iconButton.hoverBackgroundBase, 0.1)
      : 'transparent',
  },
  '&.Mui-disabled': {
    color: theme.palette.navigation.primary.iconButton.color,
    opacity: 0.5,
  },
}));

export const PrimaryNavigationSearchButtonRoot = styled(ButtonBase, {
  shouldForwardProp: shouldForwardZoomProp,
})<ZoomStyleProps>(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.brand[900],
  border: `${getCssPx(1)} solid ${theme.palette.brand[800]}`,
  borderRadius: getCssPx(theme.shape.radius.xs),
  boxSizing: 'border-box',
  color: theme.palette.neutral[400],
  display: 'inline-flex',
  flexShrink: 0,
  gap: getThemeSpacingCssPx(theme, 'xxs'),
  height: getCssPx(32),
  justifyContent: 'flex-start',
  padding: `${getThemeSpacingCssPx(theme, 'xxs')} ${getThemeSpacingCssPx(theme, 's')}`,
  transition: getPrimaryNavigationTransition(theme),
  whiteSpace: 'nowrap',
  width: getCssPx(PRIMARY_NAVIGATION_SEARCH_BUTTON_WIDTH_PX),
  ...getPrimaryNavigationChipTextStyles(theme),
  '& svg': {
    flexShrink: 0,
    fontSize: getCssPx(16),
  },
  '&:hover': {
    backgroundColor: theme.palette.brand[800],
    color: theme.palette.neutral[200],
  },
  '&:focus-visible': {
    boxShadow: `0 0 0 ${getCssPx(2)} ${theme.palette.brand[200]}`,
    outline: 'none',
  },
}));

export const PrimaryNavigationMenuRoot = styled(Popover, {
  shouldForwardProp: shouldForwardMenuProp,
})<PrimaryNavigationMenuStyleProps>(({ menuMaxWidth, menuMinWidth, menuWidth, theme, zoomLevel }) => ({
  '& .MuiPaper-root': {
    [PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR]: getPortalZoomScale(zoomLevel),
    display: 'flex',
    flexDirection: 'column',
    minWidth: getPrimaryNavigationMenuScaledPx(menuMinWidth),
    maxWidth: `min(${getPrimaryNavigationMenuScaledPx(menuMaxWidth)}, calc(100vw - ${getPrimaryNavigationMenuScaledSpacingPx(
      theme,
      4
    )}))`,
    width: menuWidth === 'content' ? 'max-content' : undefined,
    maxHeight: `calc(100vh - ${getPrimaryNavigationMenuScaledPx(PRIMARY_NAVIGATION_MENU_VIEWPORT_GUTTER_PX)})`,
    padding: getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
    borderRadius: getPrimaryNavigationMenuScaledPx(theme.shape.radius.s),
    backgroundColor: theme.palette.navigation.primary.menu.surface,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    overflowY: 'hidden',
    overscrollBehavior: 'contain',
    fontFamily: theme.typography.fontFamily,
    boxShadow: `0 ${getPrimaryNavigationMenuScaledPx(8)} ${getPrimaryNavigationMenuScaledPx(24)} ${alpha(
      theme.palette.navigation.primary.menu.shadow,
      0.16
    )}`,
    position: 'relative',
    transition: getPrimaryNavigationTransition(theme),
  },
  '& .MuiPaper-root[data-primary-navigation-menu-scroll-above-label]::before, & .MuiPaper-root[data-primary-navigation-menu-scroll-below-label]::after':
    {
      alignItems: 'center',
      backgroundColor: alpha(theme.palette.brand[700], 0.98),
      border: `${getPrimaryNavigationMenuScaledPx(1)} solid ${alpha(theme.palette.neutral.white, 0.18)}`,
      borderRadius: getPrimaryNavigationMenuScaledPx(theme.shape.radius.xs),
      boxShadow: `0 ${getPrimaryNavigationMenuScaledPx(4)} ${getPrimaryNavigationMenuScaledPx(12)} ${alpha(
        theme.palette.brand[900],
        0.22
      )}`,
      color: theme.palette.neutral.white,
      display: 'inline-flex',
      fontSize: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'fontSize'),
      fontWeight: theme.typography.fontWeightRegular,
      justifyContent: 'center',
      left: '50%',
      lineHeight: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'lineHeight'),
      padding: `${getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25)} ${getPrimaryNavigationMenuScaledSpacingPx(
        theme,
        0.75
      )}`,
      pointerEvents: 'none',
      position: 'absolute',
      transform: 'translateX(-50%)',
      transition: getPrimaryNavigationTransition(theme),
      whiteSpace: 'nowrap',
      zIndex: 3,
    },
  '& .MuiPaper-root[data-primary-navigation-menu-scroll-above-label]::before': {
    content: 'attr(data-primary-navigation-menu-scroll-above-label)',
    top: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.5),
  },
  '& .MuiPaper-root[data-primary-navigation-menu-scroll-below-label]::after': {
    bottom: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.5),
    content: 'attr(data-primary-navigation-menu-scroll-below-label)',
  },
  '& .MuiPaper-root:has([data-primary-navigation-menu-search-root="true"])[data-primary-navigation-menu-scroll-above-label]::before':
    {
      top: getPrimaryNavigationMenuScaledSpacingPx(theme, 4.5),
    },
  '& .MuiPaper-root:has([data-primary-navigation-menu-action="true"])[data-primary-navigation-menu-scroll-below-label]::after':
    {
      bottom: getPrimaryNavigationMenuScaledSpacingPx(theme, 4.5),
    },
  '& .MuiList-root': {
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    flex: '1 1 auto',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: 0,
    scrollbarGutter: 'auto',
    ...getPrismScrollbarStyles(theme, {
      thumbBorderColor: theme.palette.navigation.primary.menu.surface,
      thumbBorderWidth: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25),
      width: getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
    }),
  },
  '& .MuiList-root > [data-primary-navigation-menu-item-tone="ai"]': {
    marginTop: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.5),
  },
  '& .MuiList-root > [data-primary-navigation-menu-item-tone="ai"]:first-of-type': {
    marginTop: 0,
  },
}));

export const PrimaryNavigationMenuSearchRoot = styled(InputBase)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  minHeight: getPrimaryNavigationMenuScaledPx(26),
  boxSizing: 'border-box',
  marginBottom: getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
  padding: `${getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25)} ${getPrimaryNavigationMenuScaledSpacingPx(
    theme,
    1
  )}`,
  gap: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25),
  border: `${getPrimaryNavigationMenuScaledPx(1)} solid ${theme.palette.navigation.primary.menu.search.border}`,
  borderRadius: getPrimaryNavigationMenuScaledPx(theme.shape.radius.xs),
  backgroundColor: theme.palette.navigation.primary.menu.search.background,
  color: theme.palette.navigation.primary.menu.search.text,
  fontSize: getPrimaryNavigationMenuScaledTypographyPx(theme, 'body2', 'fontSize'),
  flexShrink: 0,
  lineHeight: getPrimaryNavigationMenuScaledTypographyPx(theme, 'body2', 'lineHeight'),
  transition: getPrimaryNavigationTransition(theme),
  '& .MuiInputBase-input': {
    height: getPrimaryNavigationMenuScaledPx(20),
    padding: 0,
    color: theme.palette.navigation.primary.menu.search.text,
    fontSize: getPrimaryNavigationMenuScaledTypographyPx(theme, 'body2', 'fontSize'),
    lineHeight: getPrimaryNavigationMenuScaledTypographyPx(theme, 'body2', 'lineHeight'),
    '&::placeholder': {
      color: theme.palette.navigation.primary.menu.search.text,
      opacity: 1,
    },
  },
}));

export const PrimaryNavigationMenuSearchIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: getPrimaryNavigationMenuScaledPx(16),
  height: getPrimaryNavigationMenuScaledPx(16),
  color: theme.palette.navigation.primary.menu.search.text,
  flexShrink: 0,
  '& svg': {
    fontSize: getPrimaryNavigationMenuScaledPx(16),
  },
}));

export const PrimaryNavigationMenuSearchClearButton = styled('button')(({ theme }) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: '50%',
  color: theme.palette.navigation.primary.menu.search.text,
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  height: getPrimaryNavigationMenuScaledPx(16),
  justifyContent: 'center',
  padding: 0,
  transition: getPrimaryNavigationTransition(theme),
  width: getPrimaryNavigationMenuScaledPx(16),
  '& svg': {
    fontSize: getPrimaryNavigationMenuScaledPx(16),
  },
  '&:hover, &:focus-visible': {
    backgroundColor: alpha(theme.palette.navigation.primary.menu.action.hoverBackgroundBase, 0.1),
    color: theme.palette.brand[700],
    outline: 'none',
  },
}));

const getPrimaryNavigationMenuActionColors = (tone: PrimaryNavigationMenuActionTone, theme: Theme) => {
  if (tone === 'danger') {
    return {
      background: alpha(theme.palette.error.main, 0.04),
      border: theme.palette.error.main,
      focusBorder: alpha(theme.palette.error.main, 0.48),
      hoverBackground: alpha(theme.palette.error.main, 0.08),
      text: theme.palette.error.main,
    };
  }

  return {
    background: theme.palette.navigation.primary.menu.action.background,
    border: theme.palette.navigation.primary.menu.action.border,
    focusBorder: theme.palette.brand[400],
    hoverBackground: alpha(theme.palette.navigation.primary.menu.action.hoverBackgroundBase, 0.06),
    text: theme.palette.navigation.primary.menu.action.text,
  };
};

export const PrimaryNavigationMenuActionRoot = styled(ButtonBase, {
  shouldForwardProp: shouldForwardMenuActionProp,
})<PrimaryNavigationMenuActionStyleProps>(({ theme, tone }) => {
  const colors = getPrimaryNavigationMenuActionColors(tone, theme);

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: getPrimaryNavigationMenuScaledPx(24),
    boxSizing: 'border-box',
    padding: `${getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25)} ${getPrimaryNavigationMenuScaledSpacingPx(
      theme,
      1
    )}`,
    gap: getPrimaryNavigationMenuScaledSpacingPx(theme, 0.25),
    border: `${getPrimaryNavigationMenuScaledPx(1)} solid ${colors.border}`,
    borderRadius: getPrimaryNavigationMenuScaledPx(theme.shape.radius.xs),
    backgroundColor: colors.background,
    color: colors.text,
    fontSize: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'fontSize'),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'lineHeight'),
    transition: getPrimaryNavigationTransition(theme),
    '&:hover': {
      backgroundColor: colors.hoverBackground,
    },
    '&.Mui-focusVisible, &:focus-visible': {
      backgroundColor: colors.hoverBackground,
      borderColor: colors.focusBorder,
      outline: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.background,
      borderColor: alpha(colors.border, 0.68),
      color: alpha(colors.text, 0.72),
      cursor: 'not-allowed',
      opacity: 1,
    },
    '& svg': {
      fontSize: getPrimaryNavigationMenuScaledPx(16),
    },
  };
});

export const PrimaryNavigationMenuActionFrame = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.navigation.primary.menu.surface,
  flexShrink: 0,
  paddingTop: getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
}));

export const PrimaryNavigationMenuActionIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: getPrimaryNavigationMenuScaledPx(16),
  height: getPrimaryNavigationMenuScaledPx(16),
  flexShrink: 0,
});

export const PrimaryNavigationMenuActionContent = styled('span')({
  display: 'block',
  flex: '0 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  textAlign: 'center',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const PrimaryNavigationMenuAiIconRoot = styled('span')({
  display: 'inline-block',
  width: getPrimaryNavigationMenuScaledPx(16),
  height: getPrimaryNavigationMenuScaledPx(16),
  flexShrink: 0,
  backgroundColor: 'currentColor',
  mask: `url("${PRIMARY_NAVIGATION_AI_ICON_SRC}") center / contain no-repeat`,
  WebkitMask: `url("${PRIMARY_NAVIGATION_AI_ICON_SRC}") center / contain no-repeat`,
});

export const PrimaryNavigationMenuItemRoot = styled(MenuItem, {
  shouldForwardProp: shouldForwardMenuItemProp,
})<PrimaryNavigationMenuItemStyleProps>(({ isActive, isHeader, theme, tone }) => ({
  display: 'flex',
  alignItems: 'center',
  gap:
    tone === 'ai' && !isHeader
      ? getPrimaryNavigationMenuScaledSpacingPx(theme, 0.5)
      : getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
  width: '100%',
  maxWidth: '100%',
  minHeight: getPrimaryNavigationMenuScaledPx(32),
  boxSizing: 'border-box',
  padding: getPrimaryNavigationMenuScaledSpacingPx(theme, 1),
  borderRadius: getPrimaryNavigationMenuScaledPx(theme.shape.radius.xs),
  color: getPrimaryNavigationMenuItemColor({ isActive, isHeader, tone }, theme),
  fontSize: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'fontSize'),
  fontStyle: getPrimaryNavigationMenuItemFontStyle({ tone }),
  fontWeight: getPrimaryNavigationMenuItemFontWeight({ isHeader, tone }, theme),
  lineHeight: getPrimaryNavigationMenuScaledTypographyPx(theme, 'caption', 'lineHeight'),
  backgroundColor: getPrimaryNavigationMenuItemBackgroundColor({ isActive, isHeader, tone }, theme),
  cursor: isHeader ? 'default' : undefined,
  overflow: tone === 'ai' && !isHeader ? 'hidden' : undefined,
  position: tone === 'ai' && !isHeader ? 'relative' : undefined,
  transition: getPrimaryNavigationTransition(theme),
  '&:hover': {
    color: getPrimaryNavigationMenuItemInteractionColor({ isHeader, tone }, theme),
    backgroundColor: getPrimaryNavigationMenuItemHoverBackgroundColor({ isActive, isHeader, tone }, theme),
  },
  '&.Mui-focusVisible, &:focus-visible': {
    color: getPrimaryNavigationMenuItemInteractionColor({ isHeader, tone }, theme),
    backgroundColor: getPrimaryNavigationMenuItemFocusBackgroundColor({ isActive, isHeader, tone }, theme),
  },
  '&.Mui-selected': {
    color: theme.palette.navigation.primary.menu.item.primaryText,
    backgroundColor: theme.palette.navigation.primary.menu.item.activeBackground,
  },
  '&.Mui-selected:hover': {
    color: theme.palette.navigation.primary.menu.item.primaryText,
    backgroundColor: theme.palette.navigation.primary.menu.item.hoverBackground,
  },
  '&.Mui-selected.Mui-focusVisible': {
    backgroundColor: theme.palette.navigation.primary.menu.item.activeBackground,
  },
  '&.Mui-disabled': {
    color: getPrimaryNavigationMenuItemDisabledColor({ isHeader, tone }, theme),
    backgroundColor:
      tone === 'danger' ? theme.palette.navigation.primary.menu.item.danger.disabledBackground : undefined,
    opacity: 1,
  },
  ...(tone === 'ai' && !isHeader
    ? {
      backgroundColor: theme.palette.neutral.white,
      boxShadow: `inset 0 0 0 ${getPrimaryNavigationMenuScaledPx(1)} ${theme.palette.ai}`,
      color: theme.palette.neutral[500],
      '&::before': {
        background: theme.palette.navigation.tertiary.aiButton.background,
        borderRadius: 'inherit',
        content: '""',
        inset: 0,
        opacity: 0,
        pointerEvents: 'none',
        position: 'absolute',
        transform: 'scaleX(0)',
        transformOrigin: 'left center',
        transition: theme.transitions.create(['opacity', 'transform'], {
          duration: theme.transitions.duration.short,
          easing: theme.transitions.easing.easeOut,
        }),
        zIndex: 0,
      },
      '& > *': {
        position: 'relative',
        zIndex: 1,
      },
      '& [data-primary-navigation-menu-leading-slot]': {
        color: theme.palette.ai,
      },
      '&:hover, &.Mui-focusVisible, &:focus-visible': {
        backgroundColor: theme.palette.neutral.white,
        boxShadow: 'none',
        color: theme.palette.navigation.tertiary.aiButton.text,
        '& [data-primary-navigation-menu-leading-slot]': {
          color: theme.palette.navigation.tertiary.aiButton.text,
        },
        '&::before': {
          opacity: 1,
          transform: 'scaleX(1)',
        },
      },
      '&.Mui-disabled': {
        background: theme.palette.neutral.white,
        boxShadow: `inset 0 0 0 ${getPrimaryNavigationMenuScaledPx(1)} ${alpha(theme.palette.ai, 0.32)}`,
        color: theme.palette.navigation.primary.menu.item.disabled,
        minHeight: getPrimaryNavigationMenuScaledPx(32),
        opacity: 1,
        '& [data-primary-navigation-menu-leading-slot]': {
          color: alpha(theme.palette.ai, 0.48),
        },
      },
    }
    : {}),
}));

export const PrimaryNavigationMenuItemLeadingSlot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: getPrimaryNavigationMenuScaledPx(16),
  height: getPrimaryNavigationMenuScaledPx(16),
  color: 'inherit',
  '& svg': {
    fontSize: getPrimaryNavigationMenuScaledPx(16),
  },
});

export const PrimaryNavigationMenuItemLeadingActionRoot = styled('span', {
  shouldForwardProp: shouldForwardMenuItemLeadingActionProp,
})<PrimaryNavigationMenuItemLeadingActionStyleProps>(({ isDisabled, isVisible, theme }) => ({
  alignItems: 'center',
  borderRadius: getPrimaryNavigationMenuScaledPx(6),
  boxSizing: 'border-box',
  color: 'inherit',
  cursor: isDisabled ? 'default' : 'pointer',
  display: 'inline-flex',
  height: getPrimaryNavigationMenuScaledPx(20),
  justifyContent: 'center',
  opacity: isVisible ? 1 : 0,
  padding: getPrimaryNavigationMenuScaledPx(2),
  pointerEvents: isDisabled ? 'none' : 'auto',
  transition: theme.transitions.create(['background-color', 'opacity'], {
    duration: theme.transitions.duration.shortest,
    easing: theme.transitions.easing.easeInOut,
  }),
  width: getPrimaryNavigationMenuScaledPx(20),
  '&:hover, &:focus-visible': {
    backgroundColor: isDisabled ? undefined : alpha(theme.palette.neutral.black, 0.1),
  },
  '& svg': {
    fontSize: getPrimaryNavigationMenuScaledPx(16),
  },
}));

export const PrimaryNavigationMenuItemContent = styled('span')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// Stacks the primary label over a secondaryLabel. Only wraps the content when a secondary line is
// present, so every existing call site keeps its exact single-line markup.
export const PrimaryNavigationMenuItemTextStack = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flex: 1,
  minWidth: 0,
});

export const PrimaryNavigationMenuItemSecondaryText = styled('span')(({ theme }) => ({
  fontSize: getThemeTypographyCssPx(theme, 'caption', 'fontSize'),
  fontWeight: theme.typography.fontWeightRegular,
  color: theme.palette.neutral[500],
  // The reason has to be readable, so this line wraps where the primary label ellipsises.
  whiteSpace: 'normal',
}));

export const PrimaryNavigationMenuItemEndIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: getPrimaryNavigationMenuScaledPx(16),
  height: getPrimaryNavigationMenuScaledPx(16),
  color: 'inherit',
  '& svg': {
    fontSize: getPrimaryNavigationMenuScaledPx(16),
  },
});
